import { FliptClient, ErrorStrategy } from "@flipt-io/flipt-client-js";
import { FLIPT_NAMESPACE } from "./FliptFlagConfig";

// Poll cycles skipped between re-init attempts while Flipt stays unreachable, doubling on each
// failure up to the cap. The first poll after a failed init retries immediately (fast recovery from
// a transient startup blip); only sustained failure backs off, so a durably-down Flipt does not fire
// a full client re-init — and log a warning — on every poll tick for the whole session.
const INITIAL_RETRY_BACKOFF_POLLS = 1;
const MAX_RETRY_BACKOFF_POLLS = 20;

type FliptInitArgs = { url: string; environment: string; namespace: string };

// The outcome of a variant evaluation. attachment carries the per-variant payload and rides along only
// on a genuine match; the code default (before-settle / non-match / Flipt-down) never carries one.
export interface VariantEvaluation {
  variant: string;
  attachment?: string;
}

export class FliptService {
  private client: FliptClient | null = null;
  private initPromise: Promise<void> | null = null;
  private initArgs: FliptInitArgs | null = null;
  private inFlightInit: Promise<void> | null = null;
  private pollsUntilRetry = 0;
  private retryBackoffPolls = INITIAL_RETRY_BACKOFF_POLLS;

  /**
   * Starts Flipt initialisation. Call without awaiting — app startup is never blocked (the provider
   * renders per-flag defaults immediately and swaps to live values once the snapshot loads). A slow
   * but successful init is adopted rather than discarded, so a remote Flipt that takes longer than a
   * frame still ends up driving evaluations.
   */
  init(url: string, environment: string, namespace: string = FLIPT_NAMESPACE): Promise<void> {
    this.initArgs = { url, environment, namespace };
    this.initPromise = this.attemptInit();
    return this.initPromise;
  }

  // One init attempt. FliptClient.init rejects when Flipt is unreachable (its first snapshot fetch
  // throws), so a client is set only on success. Concurrent callers — a poll firing while the first
  // attempt is still in flight — share the single in-flight attempt rather than opening parallel
  // connections; the guard clears once it settles so a later poll can start a fresh attempt.
  private attemptInit(): Promise<void> {
    if (this.inFlightInit) {
      return this.inFlightInit;
    }
    const args = this.initArgs;
    if (!args) {
      return Promise.resolve();
    }
    this.inFlightInit = FliptClient.init({
      url: args.url,
      namespace: args.namespace,
      environment: args.environment,
      errorStrategy: ErrorStrategy.Fallback,
      // updateInterval is a no-op in the browser (SDK auto-refresh is Node-only), so the browser
      // snapshot only changes when refresh() is called — FeatureFlagProvider drives that on a timer.
      updateInterval: 0,
    })
      .then((client) => {
        this.client = client;
      })
      .catch((error) => {
        console.warn("[FliptService] init failed — flags serve their per-flag defaults", error);
      })
      .finally(() => {
        this.inFlightInit = null;
      });
    return this.inFlightInit;
  }

  /** Resolves when init has settled — immediately if init was never called. */
  whenReady(): Promise<void> {
    return this.initPromise ?? Promise.resolve();
  }

  /**
   * Re-fetches the flag snapshot (ETag-cheap when unchanged). When no client exists yet because init
   * rejected, re-attempts init so an unreachable-at-startup session can recover.
   *
   * @returns true when the snapshot changed or a retry recovered the client; false when unchanged,
   *          never initialised, or on error.
   */
  async refresh(): Promise<boolean> {
    if (!this.client) {
      // init() was tried but rejected (Flipt unreachable at startup) — re-attempt it so the session
      // recovers without a reload once Flipt returns. A retry that lands a client counts as a change
      // so the provider swaps its per-flag defaults for live values. With no init args (init never
      // called) attemptInit is a no-op and this stays false.
      if (this.pollsUntilRetry > 0) {
        this.pollsUntilRetry--;
        return false;
      }
      await this.attemptInit();
      if (this.client) {
        this.retryBackoffPolls = INITIAL_RETRY_BACKOFF_POLLS;
        return true;
      }
      this.pollsUntilRetry = this.retryBackoffPolls;
      this.retryBackoffPolls = Math.min(this.retryBackoffPolls * 2, MAX_RETRY_BACKOFF_POLLS);
      return false;
    }
    try {
      return await this.client.refresh();
    } catch (error) {
      console.warn("[FliptService] refresh failed — keeping the current snapshot", error);
      return false;
    }
  }

  evaluate(flagKey: string, fallback: boolean, entityId: string, context?: Record<string, string>): boolean {
    if (!this.client) {
      return fallback;
    }
    try {
      const request = { flagKey, entityId, context: context ?? {} };
      return this.client.evaluateBoolean(request).enabled;
    } catch (error) {
      console.warn(`[FliptService] evaluate failed for ${flagKey}, serving fallback`, error);
      return fallback;
    }
  }

  evaluateVariant(flagKey: string, fallbackVariant: string, entityId: string, context?: Record<string, string>): VariantEvaluation {
    if (!this.client) {
      return { variant: fallbackVariant };
    }
    try {
      const request = { flagKey, entityId, context: context ?? {} };
      const result = this.client.evaluateVariant(request);
      // Keep the fallback in code, not Flipt: a non-match still carries Flipt's stored default variant,
      // so trusting it would let a misconfigured flag silently override the code default. The attachment
      // is meaningful only alongside a matched variant, so a coerced-empty payload surfaces as none.
      return result.match ? { variant: result.variantKey, attachment: result.variantAttachment || undefined } : { variant: fallbackVariant };
    } catch (error) {
      console.warn(`[FliptService] evaluateVariant failed for ${flagKey}, serving fallback`, error);
      return { variant: fallbackVariant };
    }
  }
}
